import { CaptionedImage, Card, ClassicCard, ImageOnly } from '@braze/web-sdk';
import { BrazeBottomSheetProps } from 'features/home/HomeScreen/components/BrazeBottomSheet/BrazeBottomSheet';
import {
  BrazeCardType,
  BrazeContentCard,
  BrazeSuccessProductOfferCard,
} from 'services/engagement/braze/types';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';

import { BrazeExtraKeys } from './transforms.native';

function transformURL(url: string | undefined): string | undefined {
  const cleaned = url?.replace('acme://varomoney', '');
  return cleaned;
}

function transformCardData(card: Card): BrazeContentCard | null {
  if (card instanceof ClassicCard) {
    if (
      (card.extras[BrazeExtraKeys.SUCCESS_SCREEN_ID] !== undefined &&
        card.extras[BrazeExtraKeys.SUCCESS_SCREEN_ID] !== null) ||
      card.extras[BrazeExtraKeys.BOTTOM_SHEET_CARD] === 'true'
    ) {
      return null;
    }
    return {
      id: card.id,
      type: BrazeCardType.CLASSIC,
      title: card.title,
      description: card.description,
      isDismissible: card.dismissible,
      url: transformURL(card.url),
      imageUrl: undefined,
      expiresAt: card.expiresAt ?? undefined,
      pinned: card.pinned,
      ctaTitle: card.extras[BrazeExtraKeys.PRIMARY_BUTTON_TEXT] as string,
      cardBackground: card.extras[BrazeExtraKeys.BACKGROUND_COLOR] as string,
      sdkCardWeb: card,
    };
  } else if (card instanceof CaptionedImage) {
    return {
      id: card.id,
      type: BrazeCardType.CAPTIONED_IMAGE,
      title: card.title,
      description: card.description,
      isDismissible: card.dismissible,
      url: transformURL(card.url),
      imageUrl: card.imageUrl,
      expiresAt: card.expiresAt ?? undefined,
      pinned: card.pinned,
      ctaTitle: card.extras[BrazeExtraKeys.PRIMARY_BUTTON_TEXT] as string,
      cardBackground: card.extras[BrazeExtraKeys.BACKGROUND_COLOR] as string,
      sdkCardWeb: card,
    };
  } else if (card instanceof ImageOnly) {
    return {
      id: card.id,
      type: BrazeCardType.IMAGE_ONLY,
      title: null,
      description: null,
      isDismissible: card.dismissible,
      url: transformURL(card.url),
      imageUrl: card.imageUrl,
      expiresAt: card.expiresAt ?? undefined,
      pinned: card.pinned,
      ctaTitle: card.extras[BrazeExtraKeys.PRIMARY_BUTTON_TEXT] as string,
      cardBackground: card.extras[BrazeExtraKeys.BACKGROUND_COLOR] as string,
      sdkCardWeb: card,
    };
  }
  return null;
}

export function transformBrazeContentCard(
  sdkCard: Card,
): BrazeContentCard | null {
  const card = sdkCard as Card;
  if (card.extras[BrazeExtraKeys.TYPE] === 'carousel_marketing_cards') {
    return null;
  }
  return transformCardData(card);
}

export function transformMarketingCarouselCards(
  sdkCard: Card,
): BrazeContentCard | null {
  const card = sdkCard as Card;
  if (card.extras[BrazeExtraKeys.TYPE] === 'carousel_marketing_cards') {
    return transformCardData(sdkCard);
  }
  return null;
}

export function transformBrazeBottomSheetCard(
  sdkCard: Card,
): BrazeBottomSheetProps | null {
  const card = sdkCard as Card;
  if (!(card instanceof ClassicCard)) {
    return null;
  }
  if (card.extras.bottom_sheet_card !== 'true') {
    return null;
  }
  return {
    disclosureLinkText: card.extras.disclosure_link_text as string,
    disclosureTagline: card.extras.disclosure_tagline as string,
    illustrationKey: card.extras.illustration_key as IllustrationKeyType,
    title: card.title,
    subtext: card.description,
    disclosureText: card.extras.disclosure_text as string,
    primaryButtonLabel: card.extras.primary_button_text as string,
    url: card.url,
    id: card.id ?? '',
    sdkCardWeb: card,
  };
}

export const transformBrazeSuccessProductOfferCard = (
  sdkCard: Card,
): { key: string; card: BrazeSuccessProductOfferCard } | null => {
  const card = sdkCard as Card;
  if (!(card instanceof ClassicCard)) {
    return null;
  }
  if (card.extras.success_screen_id === null) {
    return null;
  }
  return {
    key: card.extras.success_screen_id as string,
    card: {
      id: card.id,
      titleText: card.title,
      bodyText: card.description ?? undefined,
      buttonText: card.extras.primary_button_text as string,
      url: card.url,
      illustrationKey: card.extras.illustration_key as IllustrationKeyType,
      illustrationBackgroundColor: card.extras
        .illustration_background_color as string,
      sdkCardWeb: card,
    },
  };
};
